import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { TranscribedText } from './TranscribedText';
import TranslationButton from './TranslationButton';
import TranslatedText from './TranslatedText';
import { hasPreferredLanguage } from 'features/User/selectors';
import { getSequenceTranslation, getIsTranslationShowing } from 'features/Chat/selectors';
import { toggleTranslation } from 'features/Chat/slice';

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const Container = styled.div`
  ${({ theme: { colors, mq } }) => css`
    position: relative;
    display: grid;
    background-color: ${colors.neutral.white};
    border-radius: 1.5rem 1.5rem 0.25rem 1.5rem;
    padding: 2rem 2.5rem;
    max-width: 45rem;
    min-width: 30rem;
    z-index: -1;
    overflow: hidden;

    ${mq.md.down} {
      padding: 2rem;
      max-width: 70%;
      min-width: 50%;
    }
  `}
`;

interface IBotMessage {
  id: string;
  sequenceNumber: number;
}

const BotMessage: React.FC<IBotMessage> = ({ id, sequenceNumber }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isTaskConversation = location.pathname.includes('/task/');
  const userHasPreferredLanguage = useSelector(hasPreferredLanguage);
  
  // Get translation data from Redux state
  const translation = useSelector(getSequenceTranslation({ id, sequenceNumber }));
  const showTranslation = useSelector(getIsTranslationShowing({ id, sequenceNumber }));
  const isTranslationReady = !!translation;
  
  const shouldShowTranslationButton = isTaskConversation && userHasPreferredLanguage;

  const handleTranslationToggle = () => {
    dispatch(toggleTranslation({ id, sequenceNumber }));
  };

  return (
    <Wrapper>
      <Container>
        <TranscribedText id={id} sequenceNumber={sequenceNumber} />
        {showTranslation && translation && (
          <TranslatedText translation={translation} />
        )}
      </Container>
      {shouldShowTranslationButton && (
        <TranslationButton
          onClick={handleTranslationToggle}
          disabled={!isTranslationReady}
          showTranslation={showTranslation}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(BotMessage);
